<template>
  <v-container id="teams" fluid tag="section" class="tm-page dashboard-container" style="min-height: 100vh;">

    <!-- ── Cabeçalho ─────────────────────────────── -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center">
        <div class="tm-header-icon mr-3">
          <v-icon color="#22d3ee" size="22">mdi-account-group-outline</v-icon>
        </div>
        <div>
          <div class="tm-page-title">{{ $t('teams.title') }}</div>
          <div class="tm-page-sub">{{ activeTeamsCount }} times · estrutura, liderança e saúde de cada squad</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap:10px;">
        <!-- View toggle -->
        <div class="tm-view-toggle">
          <button class="tm-view-btn" :class="{ 'tm-view-btn--active': activeTab === 0 }" @click="activeTab = 0">
            <v-icon size="15" class="mr-1">mdi-view-grid-outline</v-icon>
            {{ $t('teams.list') }}
          </button>
          <button class="tm-view-btn" :class="{ 'tm-view-btn--active': activeTab === 1 }" @click="activeTab = 1">
            <v-icon size="15" class="mr-1">mdi-sitemap</v-icon>
            {{ $t('teams.orgChart') }}
          </button>
        </div>
        <v-btn v-if="!isViewOnlyMode && activeTab === 0" depressed class="tm-btn-new text-none" @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('teams.new') }}
        </v-btn>
      </div>
    </div>

    <!-- ── ABA LISTA ──────────────────────────────── -->
    <div v-if="activeTab === 0">

      <!-- Stats row -->
      <v-row class="mb-5">
        <v-col cols="12" sm="6" md="3">
          <v-card class="tm-stat-card elevation-0">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="15" class="mr-2" style="color:#22d3ee">mdi-account-group-outline</v-icon>
                <span class="tm-stat-label">{{ $t('teams.activeTeams') }}</span>
              </div>
              <div class="tm-stat-value" style="color:#22d3ee">{{ activeTeamsCount }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="tm-stat-card elevation-0">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="15" class="mr-2" style="color:#a855f7">mdi-account-multiple-outline</v-icon>
                <span class="tm-stat-label">{{ $t('teams.allocatedPeople') }}</span>
              </div>
              <div class="tm-stat-value" style="color:#a855f7">{{ totalMembersCount }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="tm-stat-card elevation-0">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="15" class="mr-2" style="color:#4ade80">mdi-shield-check-outline</v-icon>
                <span class="tm-stat-label">Compliance médio</span>
              </div>
              <div class="tm-stat-value" style="color:#4ade80">{{ averageCompliance }}%</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="tm-stat-card elevation-0">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="15" class="mr-2" style="color:#f59e0b">mdi-pulse</v-icon>
                <span class="tm-stat-label">Saúde média</span>
              </div>
              <div class="tm-stat-value" style="color:#f59e0b">{{ averageHealth }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Teams grid -->
      <v-row v-if="!loading">
        <v-col v-for="team in filteredTeams" :key="team.id" cols="12" md="6">
          <v-card class="tm-team-card elevation-0">
            <v-card-text class="pa-5">

              <!-- Nome + status -->
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <span class="tm-team-dot mr-2" :style="{ backgroundColor: team.color || '#22d3ee' }"></span>
                  <span class="tm-team-name">{{ team.name }}</span>
                </div>
                <span class="tm-status-badge" :class="team.status === 'ACTIVE' ? 'tm-status-badge--active' : 'tm-status-badge--arch'">
                  {{ team.status === 'ACTIVE' ? 'Ativo' : 'Arquivado' }}
                </span>
              </div>

              <!-- Descrição -->
              <div class="tm-team-desc mb-4">{{ team.description || 'Sem descrição' }}</div>

              <!-- Avatares + Lead -->
              <div class="d-flex align-center justify-space-between mb-4">
                <div class="avatar-group">
                  <template v-if="team.members && team.members.length > 0">
                    <v-tooltip v-for="(member, idx) in team.members.slice(0, 5)" :key="idx" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar
                          size="28"
                          class="tm-avatar-overlap"
                          :style="{ zIndex: 10 - idx, marginLeft: idx > 0 ? '-8px' : '0' }"
                          v-bind="attrs" v-on="on"
                        >
                          <v-img v-if="member.avatarUrl" :src="member.avatarUrl" />
                          <span v-else class="white--text" style="font-size:10px; font-weight:700; width:100%; height:100%; display:flex; align-items:center; justify-content:center;" :style="{ background: getAvatarColor(member.name) }">
                            {{ getInitials(member.name) }}
                          </span>
                        </v-avatar>
                      </template>
                      <span>{{ member.name }}</span>
                    </v-tooltip>
                    <v-avatar v-if="team.members.length > 5" size="28" class="tm-avatar-overlap" style="z-index:1; margin-left:-8px; background:#2a2a2a;">
                      <span style="font-size:10px; color:#aaa; font-weight:700;">+{{ team.members.length - 5 }}</span>
                    </v-avatar>
                  </template>
                  <span v-else class="tm-stat-label">Sem membros</span>
                </div>
                <div class="tm-lead-text">
                  Lead: <span class="font-weight-medium">{{ team.leaderName || '—' }}</span>
                </div>
              </div>

              <!-- Métricas -->
              <v-divider class="tm-card-divider mb-3" />
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="tm-metric-label">Lead time</div>
                  <div class="tm-metric-value">{{ team.leadTime || '—' }}</div>
                </div>
                <div>
                  <div class="tm-metric-label">Deploys</div>
                  <div class="tm-metric-value">{{ team.deploysPerDay ? team.deploysPerDay + '/dia' : '—' }}</div>
                </div>
                <div>
                  <div class="tm-metric-label">Saúde</div>
                  <div class="d-flex align-center">
                    <span class="tm-metric-value" :style="{ color: getHealthColor(team.healthScore) }">{{ team.healthScore || (team.compliance || 0) }}</span>
                    <span v-if="team.healthTrend" class="ml-1">
                      <v-icon x-small :color="team.healthTrend > 0 ? '#4ade80' : '#f87171'">{{ team.healthTrend > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
                      <span style="font-size:11px;" :style="{ color: team.healthTrend > 0 ? '#4ade80' : '#f87171' }">{{ team.healthTrend > 0 ? '+' : '' }}{{ team.healthTrend }}</span>
                    </span>
                  </div>
                </div>
                <!-- Ações (aparecem no hover do card) -->
                <div v-if="!isViewOnlyMode" class="tm-card-actions d-flex align-center" style="gap:2px;">
                  <v-btn icon x-small class="tm-action-btn" @click="viewMembers(team)">
                    <v-icon size="15">mdi-account-multiple-outline</v-icon>
                  </v-btn>
                  <v-btn icon x-small class="tm-action-btn" @click="editTeam(team)">
                    <v-icon size="15">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon x-small class="tm-action-btn tm-action-btn--delete" @click="confirmDeleteTeam(team)">
                    <v-icon size="15">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>

            </v-card-text>
          </v-card>
        </v-col>

        <!-- Empty state -->
        <v-col v-if="filteredTeams.length === 0" cols="12">
          <div class="text-center py-12">
            <v-icon size="64" style="color:rgba(255,255,255,0.08)">mdi-account-group-outline</v-icon>
            <div class="tm-stat-label mt-4 mb-4">Nenhum time encontrado</div>
            <v-btn v-if="!isViewOnlyMode" depressed class="tm-btn-new text-none" @click="openCreateDialog">
              <v-icon left small>mdi-plus</v-icon>
              Criar Primeiro Time
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Loading -->
      <div v-else class="text-center py-12">
        <v-progress-circular indeterminate color="#22d3ee" size="40" />
        <div class="tm-stat-label mt-4">Carregando times...</div>
      </div>

    </div>

    <!-- ── ABA ORGANOGRAMA ────────────────────────── -->
    <div v-else>
      <div v-if="loadingOrgChart" class="text-center py-12">
        <v-progress-circular indeterminate color="#22d3ee" size="40" />
        <div class="tm-stat-label mt-4">Carregando organograma...</div>
      </div>

      <div v-else class="org-chart-container mt-4">
        <!-- Admins -->
        <div v-if="orgChart.admins && orgChart.admins.length > 0" class="org-level">
          <div class="org-nodes-wrapper">
            <div v-for="admin in orgChart.admins" :key="admin.id" class="org-node admin-node">
              <v-card elevation="0" class="org-card">
                <v-card-text class="text-center pa-3">
                  <v-avatar :color="getAvatarColor(admin.name)" size="60" class="mb-2">
                    <v-img v-if="admin.avatarUrl" :src="admin.avatarUrl" />
                    <span v-else class="white--text text-h6">{{ getInitials(admin.name) }}</span>
                  </v-avatar>
                  <div class="text-subtitle-2 font-weight-bold">{{ admin.name }}</div>
                  <div class="text-caption text--secondary">{{ admin.role === 'TENANT_ADMIN' ? 'Admin' : admin.jobRoleName || 'Sem cargo' }}</div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- Managers -->
        <div v-if="orgChart.managers && orgChart.managers.length > 0" class="org-level">
          <div class="org-nodes-wrapper">
            <div v-for="manager in orgChart.managers" :key="manager.id" class="org-node manager-node">
              <v-card elevation="0" class="org-card">
                <v-card-text class="text-center pa-3">
                  <v-avatar :color="getAvatarColor(manager.name)" size="56" class="mb-2">
                    <v-img v-if="manager.avatarUrl" :src="manager.avatarUrl" />
                    <span v-else class="white--text text-subtitle-1">{{ getInitials(manager.name) }}</span>
                  </v-avatar>
                  <div class="text-subtitle-2 font-weight-bold">{{ manager.name }}</div>
                  <div class="text-caption text--secondary">{{ manager.jobRoleName || 'Gestor' }}</div>
                  <div v-if="manager.departmentName" class="text-caption grey--text">{{ manager.departmentName }}</div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- Users -->
        <div v-if="orgChart.users && orgChart.users.length > 0" class="org-level">
          <div class="org-nodes-wrapper">
            <div v-for="user in orgChart.users" :key="user.id" class="org-node user-node">
              <v-card elevation="0" class="org-card">
                <v-card-text class="text-center pa-3">
                  <v-avatar :color="getAvatarColor(user.name)" size="48" class="mb-2">
                    <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                    <span v-else class="white--text text-body-2">{{ getInitials(user.name) }}</span>
                  </v-avatar>
                  <div class="text-body-2 font-weight-medium">{{ user.name }}</div>
                  <div class="text-caption text--secondary">{{ user.jobRoleName || 'Colaborador' }}</div>
                  <div v-if="user.departmentName" class="text-caption grey--text">{{ user.departmentName }}</div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <div v-if="!orgChart.admins?.length && !orgChart.managers?.length && !orgChart.users?.length" class="text-center py-12">
          <v-icon size="64" style="color:rgba(255,255,255,0.08)">mdi-sitemap</v-icon>
          <div class="tm-stat-label mt-4">Organograma vazio — nenhum colaborador cadastrado</div>
        </div>
      </div>
    </div>

    <!-- Dialog: Criar/Editar Time -->
    <v-dialog
      v-model="teamDialog"
      max-width="600px"
      persistent
    >
      <v-card rounded="lg">
        <v-card-title class="headline" :style="`background: ${editingTeam ? '#22d3ee' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}; color: white`">
          <v-icon left color="white">{{ editingTeam ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ editingTeam ? 'Editar Time' : 'Novo Time' }}
        </v-card-title>

        <v-card-text class="pt-6 pb-2">
          <v-form ref="teamForm" v-model="validTeam">
            <v-row>
              <!-- Nome -->
              <v-col cols="12">
                <v-text-field
                  v-model="teamData.name"
                  label="Nome do Time *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-group"
                  hint="Ex: Squad Dragons, Tribo Phoenix, Equipe Titans"
                  persistent-hint
                />
              </v-col>

              <!-- Descrição -->
              <v-col cols="12">
                <v-textarea
                  v-model="teamData.description"
                  label="Descrição"
                  outlined
                  dense
                  rows="2"
                  prepend-inner-icon="mdi-text"
                  hint="Propósito e objetivos do time"
                  persistent-hint
                />
              </v-col>

              <!-- Líder -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="teamData.leaderId"
                  :items="users"
                  item-text="name"
                  item-value="id"
                  label="Líder do Time *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-star"
                  clearable
                >
                  <template v-slot:selection="{ item }">
                    <v-avatar size="24" class="mr-2" color="grey lighten-2">
                      <v-img v-if="item.avatarUrl" :src="item.avatarUrl" />
                      <span v-else class="text-caption">{{ getInitials(item.name) }}</span>
                    </v-avatar>
                    {{ item.name }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar>
                      <v-img v-if="item.avatarUrl" :src="item.avatarUrl" />
                      <v-avatar v-else color="grey lighten-2" size="32">
                        <span class="text-caption">{{ getInitials(item.name) }}</span>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.jobRoleName || 'Sem cargo' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Departamento Primário -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="teamData.departmentId"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Departamento Primário"
                  outlined
                  dense
                  prepend-inner-icon="mdi-domain"
                  clearable
                  hint="Opcional - times podem ser cross-departamentais"
                  persistent-hint
                />
              </v-col>

              <!-- Cor -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="teamData.color"
                  :items="colorOptions"
                  label="Cor do Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-palette"
                >
                  <template v-slot:selection="{ item }">
                    <v-avatar :color="item.value" size="20" class="mr-2" />
                    {{ item.text }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-avatar :color="item.value" size="24" class="mr-3" />
                    {{ item.text }}
                  </template>
                </v-select>
              </v-col>

              <!-- Ícone -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="teamData.icon"
                  :items="iconOptions"
                  label="Ícone do Time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-emoticon"
                >
                  <template v-slot:selection="{ item }">
                    <v-icon :color="teamData.color || '#22d3ee'" class="mr-2">{{ item.value }}</v-icon>
                    {{ item.text }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-icon class="mr-3">{{ item.value }}</v-icon>
                    {{ item.text }}
                  </template>
                </v-select>
              </v-col>

              <!-- Status -->
              <v-col v-if="editingTeam" cols="12">
                <v-select
                  v-model="teamData.status"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shield-check"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            @click="closeTeamDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#22d3ee"
            dark
            :loading="saving"
            @click="saveTeam"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Membros do Time -->
    <v-dialog
      v-model="membersDialog"
      max-width="800px"
      scrollable
    >
      <v-card rounded="lg">
        <v-card-title 
          class="headline white--text justify-center" 
          :style="`background: ${selectedTeam?.color || '#22d3ee'}`"
        >
          <v-icon left color="white">mdi-account-multiple</v-icon>
          {{ selectedTeam?.status === 'ARCHIVED' ? 'Membros antigos' : 'Membros' }}: {{ selectedTeam?.name }}
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Botão Adicionar Membros -->
          <div v-if="!isViewOnlyMode" class="d-flex justify-center mb-4">
            <v-tooltip bottom :disabled="selectedTeam?.status !== 'ARCHIVED'">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    color="#22d3ee"
                    dark
                    rounded
                    :disabled="selectedTeam?.status === 'ARCHIVED'"
                    @click="openAddMembersDialog"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Adicionar Membros
                  </v-btn>
                </span>
              </template>
              <span>Time arquivado — não é possível adicionar novos membros</span>
            </v-tooltip>
          </div>

          <v-list class="pa-0">
            <v-list-item
              v-for="(member, index) in teamMembers"
              :key="member.userId || member.id || index"
              class="border-bottom"
            >
              <v-list-item-avatar>
                <v-img v-if="member.avatarUrl" :src="member.avatarUrl" />
                <v-avatar v-else :color="member.teamRole === 'LEADER' ? '#22d3ee' : 'grey lighten-2'">
                  <span class="white--text">{{ getInitials(member.name) }}</span>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ member.name }}
                  <v-chip
                    v-if="member.teamRole === 'LEADER'"
                    x-small
                    color="#22d3ee"
                    dark
                    class="ml-2"
                  >
                    <v-icon x-small left>mdi-star</v-icon>
                    Líder
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-icon x-small class="mr-1">mdi-briefcase</v-icon>
                  {{ (member.jobRole && member.jobRole.title) || member.jobRoleName || 'Sem cargo' }}
                  <span class="mx-2">•</span>
                  <v-icon x-small class="mr-1">mdi-domain</v-icon>
                  {{ (member.department && member.department.name) || member.departmentName || 'Sem departamento' }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  v-if="!isViewOnlyMode && member.teamRole !== 'LEADER'"
                  icon
                  small
                  color="error"
                  @click="confirmRemoveMember(member)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <!-- Empty State -->
            <v-list-item v-if="teamMembers.length === 0">
              <v-list-item-content class="text-center py-8">
                <v-icon size="60" color="grey lighten-2">mdi-account-off</v-icon>
                <p class="grey--text mt-4">Nenhum membro alocado neste time</p>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            @click="membersDialog = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Adicionar Membros -->
    <v-dialog
      v-model="addMembersDialog"
      max-width="600px"
      persistent
    >
      <v-card rounded="lg">
        <v-card-title class="headline" style="background: #FF771C; color: white">
          <v-icon left color="white">mdi-account-plus</v-icon>
          Adicionar Membros ao Time
        </v-card-title>

        <v-card-text class="pt-6">
          <v-autocomplete
            v-model="selectedUserIds"
            :items="availableUsers"
            item-text="name"
            item-value="id"
            label="Selecione os usuários"
            outlined
            dense
            multiple
            chips
            deletable-chips
            prepend-inner-icon="mdi-account-multiple"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 3"
                small
                close
                @click:close="removeSelectedUser(item.id)"
              >
                <v-avatar left size="24">
                  <v-img v-if="item.avatarUrl" :src="item.avatarUrl" />
                  <span v-else class="text-caption">{{ getInitials(item.name) }}</span>
                </v-avatar>
                {{ item.name }}
              </v-chip>
              <span
                v-if="index === 3"
                class="grey--text text-caption ml-2"
              >
                (+{{ selectedUserIds.length - 3 }} outros)
              </span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-avatar>
                <v-img v-if="item.avatarUrl" :src="item.avatarUrl" />
                <v-avatar v-else color="grey lighten-2" size="32">
                  <span class="text-caption">{{ getInitials(item.name) }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ (item.department && item.department.name) || 'Sem dept.' }} • {{ (item.jobRole && item.jobRole.title) || 'Sem cargo' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            @click="addMembersDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#22d3ee"
            dark
            :loading="saving"
            :disabled="selectedUserIds.length === 0"
            @click="addMembers"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Adicionar ({{ selectedUserIds.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar Remoção de Membro -->
    <v-dialog
      v-model="confirmDeleteMemberDialog"
      max-width="500px"
      persistent
    >
      <v-card rounded="lg">
        <v-card-title class="headline white--text" style="background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%)">
          <v-icon left color="white">mdi-alert-circle-outline</v-icon>
          Remover Membro do Time
        </v-card-title>

        <v-card-text class="pt-6 pb-4">
          <div class="text-center">
            <v-icon size="60" color="error" class="mb-4">mdi-account-remove</v-icon>
            <h3 class="text-h6 font-weight-bold mb-3">
              Tem certeza que deseja remover?
            </h3>
            <p class="text-body-1 mb-2">
              <strong>{{ memberToRemove?.name }}</strong>
            </p>
            <p class="text-caption grey--text">
              será removido(a) do time <strong>{{ selectedTeam?.name }}</strong>
            </p>
          </div>

          <v-alert
            type="warning"
            text
            dense
            class="mt-4"
          >
            <div class="text-caption">
              <v-icon small left>mdi-information-outline</v-icon>
              Esta ação pode ser desfeita adicionando o membro novamente ao time.
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            @click="confirmDeleteMemberDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            dark
            :loading="saving"
            @click="removeMember"
          >
            <v-icon left>mdi-delete</v-icon>
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar Exclusão de Time -->
    <v-dialog
      v-model="confirmDeleteTeamDialog"
      max-width="550px"
      persistent
    >
      <v-card rounded="lg">
        <v-card-title class="headline white--text" style="background: linear-gradient(135deg, #e53935 0%, #d32f2f 100%)">
          <v-icon left color="white">mdi-alert-circle-outline</v-icon>
          Remover Time
        </v-card-title>

        <v-card-text class="pt-6 pb-4">
          <div class="text-center">
            <v-icon size="60" color="error" class="mb-4">mdi-delete-alert</v-icon>
            <h3 class="text-h6 font-weight-bold mb-3">
              Tem certeza que deseja deletar este time?
            </h3>
            <p class="text-body-1 mb-2">
              <strong>{{ teamToDelete?.name }}</strong>
            </p>
            <p class="text-caption grey--text mb-4">
              {{ teamToDelete?.description || 'Sem descrição' }}
            </p>
          </div>

          <v-alert
            type="error"
            text
            dense
            class="mt-4"
            icon="mdi-alert"
          >
            <div class="text-caption">
              <strong>ATENÇÃO:</strong> Esta ação é permanente e não pode ser desfeita.<br>
              O time só pode ser removido se não tiver membros alocados.
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            text
            @click="confirmDeleteTeamDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            dark
            :loading="saving"
            @click="deleteTeam"
          >
            <v-icon left>mdi-delete-forever</v-icon>
            Deletar Time
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      bottom
      right
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { TeamsService, UsersService, OrganizationService } from '@/services'

export default {
  name: 'Teams',

  data: () => ({
    loading: false,
    saving: false,

    // Tabs
    activeTab: 0,

    // Organograma
    loadingOrgChart: false,
    orgChart: {
      admins: [],
      managers: [],
      users: [],
    },

    // Filters
    search: '',
    statusFilter: 'ALL',

    // Data
    teams: [],
    users: [],
    departments: [],

    // Dialogs
    teamDialog: false,
    membersDialog: false,
    addMembersDialog: false,
    confirmDeleteMemberDialog: false,
    confirmDeleteTeamDialog: false,

    // Editing
    editingTeam: null,
    selectedTeam: null,
    teamMembers: [],
    selectedUserIds: [],
    memberToRemove: null,
    teamToDelete: null,

    validTeam: false,
    teamData: {
      name: '',
      description: '',
      leaderId: null,
      departmentId: null,
      color: '#22d3ee',
      icon: 'mdi-account-group',
      status: 'ACTIVE',
    },
    defaultTeamData: {
      name: '',
      description: '',
      leaderId: null,
      departmentId: null,
      color: '#22d3ee',
      icon: 'mdi-account-group',
      status: 'ACTIVE',
    },

    // Options
    colorOptions: [
      { text: 'Laranja', value: '#22d3ee' },
      { text: 'Roxo', value: '#667eea' },
      { text: 'Rosa', value: '#f093fb' },
      { text: 'Azul', value: '#4facfe' },
      { text: 'Verde', value: '#4CAF50' },
      { text: 'Vermelho', value: '#fa709a' },
      { text: 'Amarelo', value: '#feca57' },
      { text: 'Turquesa', value: '#00d2d3' },
    ],
    iconOptions: [
      { text: 'Grupo', value: 'mdi-account-group' },
      { text: 'Estrela', value: 'mdi-star' },
      { text: 'Foguete', value: 'mdi-rocket' },
      { text: 'Diamante', value: 'mdi-diamond-stone' },
      { text: 'Troféu', value: 'mdi-trophy' },
      { text: 'Raio', value: 'mdi-flash' },
      { text: 'Escudo', value: 'mdi-shield' },
      { text: 'Coração', value: 'mdi-heart' },
    ],
    statusOptions: [
      { text: 'Ativo', value: 'ACTIVE' },
      { text: 'Arquivado', value: 'ARCHIVED' },
    ],

    // Validation
    rules: {
      required: v => !!v || 'Campo obrigatório',
    },

    // Snackbar
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    ...mapGetters(['isViewOnlyMode']),

    filteredTeams() {
      let result = this.teams

      // Filter by status
      if (this.statusFilter !== 'ALL') {
        result = result.filter(t => t.status === this.statusFilter)
      }

      // Filter by search
      if (this.search) {
        const search = this.search.toLowerCase()
        result = result.filter(t =>
          t.name.toLowerCase().includes(search) ||
          t.description?.toLowerCase().includes(search) ||
          t.leaderName?.toLowerCase().includes(search)
        )
      }

      return result
    },

    activeTeamsCount() {
      return this.teams.filter(t => t.status === 'ACTIVE').length
    },

    totalMembersCount() {
      return this.teams.reduce((sum, t) => sum + (t.pessoas || 0), 0)
    },

    crossDeptTeamsCount() {
      return this.teams.filter(t => t.tipo === 'Cross-Dept').length
    },

    averageCompliance() {
      if (this.teams.length === 0) return 0
      const sum = this.teams.reduce((acc, t) => acc + (t.compliance || 0), 0)
      return Math.round(sum / this.teams.length)
    },

    averageHealth() {
      const withScore = this.teams.filter(t => t.healthScore != null || t.compliance != null)
      if (withScore.length === 0) return 0
      return Math.round(withScore.reduce((sum, t) => sum + (t.healthScore || t.compliance || 0), 0) / withScore.length)
    },

    availableUsers() {
      // Usuários que ainda não estão no time
      const memberIds = this.teamMembers.map(m => m.userId)
      return this.users.filter(u => !memberIds.includes(u.id))
    },
  },

  watch: {
    activeTab(newTab) {
      // Quando alternar para a aba de Organograma (índice 1), carregar os dados
      if (newTab === 1 && this.orgChart.admins.length === 0) {
        this.loadOrganizationChart()
      }
    },
  },

  mounted() {
    this.loadAllData()
  },

  methods: {
    async loadAllData() {
      await Promise.all([
        this.loadTeams(),
        this.loadUsers(),
        this.loadDepartments(),
      ])
    },

    async loadOrganizationChart() {
      this.loadingOrgChart = true
      try {
        const response = await TeamsService.getOrganizationChart()
        this.orgChart = response.data || { admins: [], managers: [], users: [] }
        console.log('✅ Organograma carregado:', this.orgChart)
      } catch (error) {
        console.error('❌ Erro ao carregar organograma:', error)
        this.$store.commit('showSnackbar', {
          text: 'Erro ao carregar organograma',
          color: 'error',
        })
      } finally {
        this.loadingOrgChart = false
      }
    },

    async loadTeams() {
      this.loading = true
      try {
        const response = await TeamsService.list()
        this.teams = response.data || []
        console.log('✅ Times carregados:', this.teams.length)
        
        // Carregar membros para cada time (em paralelo, apenas primeiros 10 times ativos)
        this.loadTeamMembers()
      } catch (error) {
        console.error('❌ Erro ao carregar times:', error)
        this.showSnackbar('Erro ao carregar times', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadTeamMembers() {
      // Carregar membros para todos os times (ativos e arquivados)
      const activeTeams = this.teams
        .slice(0, 12) // Limitar aos primeiros 12 times
      
      // Carregar membros em paralelo
      const memberPromises = activeTeams.map(async (team) => {
        try {
          const response = await TeamsService.getMembers(team.id)
          const members = response.data || []
          
          // Atualizar o time com os membros (apenas primeiros 7 para os avatares)
          const teamIndex = this.teams.findIndex(t => t.id === team.id)
          if (teamIndex !== -1) {
            this.$set(this.teams[teamIndex], 'members', members.slice(0, 7))
          }
        } catch (error) {
          console.warn(`⚠️ Não foi possível carregar membros do time ${team.name}:`, error)
        }
      })

      await Promise.all(memberPromises)
      console.log('✅ Membros dos times carregados')
    },

    async loadUsers() {
      try {
        // Solicita dados expandidos (jobRole e department)
        const response = await UsersService.list({ expand: 'jobRole,department' })
        this.users = response.data || []
        console.log('✅ Usuários carregados:', this.users.length)
      } catch (error) {
        console.error('❌ Erro ao carregar usuários:', error)
      }
    },

    async loadDepartments() {
      try {
        const response = await OrganizationService.departments.list()
        const allDepartments = response.data || []
        // Filtrar apenas departamentos ativos
        this.departments = allDepartments.filter(dept => dept.active === true)
        console.log('✅ Departamentos carregados:', this.departments.length)
      } catch (error) {
        console.error('❌ Erro ao carregar departamentos:', error)
      }
    },

    openCreateDialog() {
      this.editingTeam = null
      this.teamData = { ...this.defaultTeamData }
      this.teamDialog = true
    },

    editTeam(team) {
      this.editingTeam = team
      this.teamData = {
        name: team.name,
        description: team.description,
        leaderId: team.leaderId,
        departmentId: team.departmentId,
        color: team.color,
        icon: team.icon,
        status: team.status,
      }
      this.teamDialog = true
    },

    closeTeamDialog() {
      this.teamDialog = false
      this.editingTeam = null
      this.$refs.teamForm?.resetValidation()
    },

    async saveTeam() {
      if (!this.$refs.teamForm.validate()) {
        return
      }

      this.saving = true

      try {
        if (this.editingTeam) {
          // Update
          console.log('📝 Atualizando time:', this.teamData)
          await TeamsService.update(this.editingTeam.id, this.teamData)
          this.showSnackbar('Time atualizado com sucesso!', 'success')
        } else {
          // Create
          console.log('✨ Criando novo time:', this.teamData)
          await TeamsService.create(this.teamData)
          this.showSnackbar('Time criado com sucesso!', 'success')
        }

        await this.loadTeams()
        this.closeTeamDialog()
      } catch (error) {
        console.error('❌ Erro ao salvar time:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao salvar time',
          'error'
        )
      } finally {
        this.saving = false
      }
    },

    async viewMembers(team) {
      this.selectedTeam = team
      this.teamMembers = []
      
      try {
        const response = await TeamsService.getMembers(team.id)
        this.teamMembers = response.data || []
        console.log('✅ Membros carregados:', this.teamMembers.length)
        console.log('🔍 Estrutura COMPLETA dos membros:', JSON.stringify(this.teamMembers, null, 2))
        if (this.teamMembers[0]) {
          console.log('🔍 Campos disponíveis:', Object.keys(this.teamMembers[0]))
        }
        this.membersDialog = true
      } catch (error) {
        console.error('❌ Erro ao carregar membros:', error)
        this.showSnackbar('Erro ao carregar membros do time', 'error')
      }
    },

    openAddMembersDialog() {
      this.selectedUserIds = []
      this.addMembersDialog = true
    },

    removeSelectedUser(userId) {
      const index = this.selectedUserIds.indexOf(userId)
      if (index !== -1) {
        this.selectedUserIds.splice(index, 1)
      }
    },

    async addMembers() {
      this.saving = true

      try {
        await TeamsService.addMembers(this.selectedTeam.id, this.selectedUserIds)
        
        this.showSnackbar(`${this.selectedUserIds.length} membro(s) adicionado(s) com sucesso!`, 'success')
        
        // Recarregar membros do time
        await this.viewMembers(this.selectedTeam)
        
        // Recarregar lista de times para atualizar contagem
        await this.loadTeams()
        
        this.addMembersDialog = false
      } catch (error) {
        console.error('❌ Erro ao adicionar membros:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao adicionar membros',
          'error'
        )
      } finally {
        this.saving = false
      }
    },

    confirmRemoveMember(member) {
      this.memberToRemove = member
      this.confirmDeleteMemberDialog = true
    },

    async removeMember() {
      this.saving = true

      console.log('🔍 Removendo membro:', this.memberToRemove)
      console.log('🔍 userId:', this.memberToRemove?.userId)
      console.log('🔍 id:', this.memberToRemove?.id)

      const userId = this.memberToRemove?.userId || this.memberToRemove?.id
      
      if (!userId) {
        this.showSnackbar('Erro: ID do usuário não encontrado', 'error')
        this.saving = false
        return
      }

      try {
        await TeamsService.removeMember(this.selectedTeam.id, userId)
        
        this.showSnackbar('Membro removido do time com sucesso!', 'success')
        
        // Fechar dialog
        this.confirmDeleteMemberDialog = false
        this.memberToRemove = null
        
        // Recarregar membros
        await this.viewMembers(this.selectedTeam)
        
        // Recarregar lista de times para atualizar contagem
        await this.loadTeams()
      } catch (error) {
        console.error('❌ Erro ao remover membro:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao remover membro',
          'error'
        )
      } finally {
        this.saving = false
      }
    },

    confirmDeleteTeam(team) {
      this.teamToDelete = team
      this.confirmDeleteTeamDialog = true
    },

    async deleteTeam() {
      this.saving = true

      try {
        await TeamsService.delete(this.teamToDelete.id)
        
        this.showSnackbar('Time removido com sucesso!', 'success')
        
        // Fechar dialog
        this.confirmDeleteTeamDialog = false
        this.teamToDelete = null
        
        // Recarregar lista de times
        await this.loadTeams()
      } catch (error) {
        console.error('❌ Erro ao remover time:', error)
        
        // Tratar erro específico de time com membros (409 Conflict)
        let errorMessage = 'Erro ao remover time'
        
        if (error.response?.status === 409) {
          errorMessage = `❌ Não é possível remover o time "${this.teamToDelete.name}". Remova todos os membros antes de excluir o time.`
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data) {
          errorMessage = error.response.data
        }
        
        this.showSnackbar(errorMessage, 'error')
      } finally {
        this.saving = false
      }
    },

    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getAvatarColor(name) {
      if (!name) return '#9E9E9E'
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
        '#98D8C8', '#6C5CE7', '#A29BFE', '#FD79A8',
        '#FDCB6E', '#6C5CE7', '#00B894', '#0984E3'
      ]
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    getHealthColor(score) {
      if (!score) return '#888'
      if (score >= 80) return '#4ade80'
      if (score >= 60) return '#f59e0b'
      return '#f87171'
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}
/* ── Page ────────────────────────────────── */
.tm-page { padding: 24px 96px; }

/* ── Header ──────────────────────────────── */
.tm-header-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.12); flex-shrink: 0;
}
.tm-page-title { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.2; }
.tm-page-sub { font-size: 13px; margin-top: 2px; }
.theme--dark .tm-page-title { color: #f5f5f5; }
.theme--light .tm-page-title { color: #1D1D1F; }
.theme--dark .tm-page-sub { color: rgba(255,255,255,0.45); }
.theme--light .tm-page-sub { color: rgba(0,0,0,0.5); }

/* ── View toggle ─────────────────────────── */
.tm-view-toggle {
  display: flex; align-items: center;
  border-radius: 8px; overflow: hidden;
  border: 1px solid rgba(128,128,128,0.2);
}
.tm-view-btn {
  display: inline-flex; align-items: center;
  padding: 6px 14px; font-size: 13px; font-weight: 500;
  border: none; background: transparent; cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.theme--dark .tm-view-btn { color: rgba(255,255,255,0.5); }
.theme--light .tm-view-btn { color: rgba(0,0,0,0.5); }
.theme--dark .tm-view-btn--active { background: #1f1f1f; color: #22d3ee; }
.theme--light .tm-view-btn--active { background: #f0f0f0; color: #22d3ee; }

/* ── Button ──────────────────────────────── */
.tm-btn-new { background-color: #22d3ee !important; color: #06222b !important; font-weight: 700 !important; border-radius: 8px !important; }

/* ── Stat cards ──────────────────────────── */
.tm-stat-card { border-radius: 12px !important; }
.theme--dark .tm-stat-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }
.theme--light .tm-stat-card.v-card { background: #ffffff !important; border: 1px solid #f0f0f0 !important; }
.tm-stat-label { font-size: 12px; color: #888; }
.tm-stat-value { font-size: 36px; font-weight: 700; line-height: 1.1; }

/* ── Team cards ──────────────────────────── */
.tm-team-card { border-radius: 12px !important; transition: box-shadow 0.2s; }
.theme--dark .tm-team-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }
.theme--light .tm-team-card.v-card { background: #ffffff !important; border: 1px solid #eeeeee !important; }
.tm-team-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.3) !important; }

.tm-team-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.tm-team-name { font-size: 15px; font-weight: 700; }
.theme--dark .tm-team-name { color: rgba(255,255,255,0.92); }
.theme--light .tm-team-name { color: #1D1D1F; }

.tm-team-desc {
  font-size: 12.5px; line-height: 1.45;
  display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.theme--dark .tm-team-desc { color: rgba(255,255,255,0.45); }
.theme--light .tm-team-desc { color: rgba(0,0,0,0.5); }

/* Status badge */
.tm-status-badge { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 12px; }
.tm-status-badge--active { background: rgba(34,197,94,0.15); color: #4ade80; }
.tm-status-badge--arch  { background: rgba(128,128,128,0.12); color: #888; }

/* Avatars */
.avatar-group { display: flex; align-items: center; }
.tm-avatar-overlap { border: 2px solid #111111; }
.theme--light .tm-avatar-overlap { border-color: #ffffff; }

/* Lead */
.tm-lead-text { font-size: 12px; }
.theme--dark .tm-lead-text { color: rgba(255,255,255,0.45); }
.theme--light .tm-lead-text { color: rgba(0,0,0,0.5); }

/* Metrics */
.tm-card-divider { border-color: rgba(128,128,128,0.12) !important; }
.tm-metric-label { font-size: 11px; color: #666; margin-bottom: 2px; }
.tm-metric-value { font-size: 13px; font-weight: 600; }
.theme--dark .tm-metric-value { color: rgba(255,255,255,0.75); }
.theme--light .tm-metric-value { color: #333; }

/* Card hover actions */
.tm-card-actions { opacity: 0; transition: opacity 0.15s; }
.tm-team-card:hover .tm-card-actions { opacity: 1; }
.tm-action-btn { transition: background 0.15s; }
.theme--dark .tm-action-btn { color: rgba(255,255,255,0.5) !important; }
.theme--dark .tm-action-btn:hover { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.9) !important; }
.tm-action-btn--delete:hover { color: #f87171 !important; }

/* ── Dialogs ─────────────────────────────── */
.border-bottom { border-bottom: 1px solid #e0e0e0; }
.theme--dark .border-bottom { border-bottom: 1px solid #2a2a2a; }

/* ── Organograma ─────────────────────────── */
.org-chart-container { width: 100%; overflow-x: auto; padding: 20px 0; }

.org-level { display: flex; justify-content: center; margin-bottom: 50px; position: relative; }
.org-level:not(:last-child)::after {
  content: ''; position: absolute; bottom: -25px; left: 50%; transform: translateX(-50%);
  width: 2px; height: 25px;
}
.theme--dark .org-level:not(:last-child)::after { background: #2a2a2a; }
.theme--light .org-level:not(:last-child)::after { background: #e0e0e0; }

.org-nodes-wrapper {
  display: flex; gap: 24px; flex-wrap: wrap;
  justify-content: center; max-width: 100%; position: relative;
}
.org-nodes-wrapper::before {
  content: ''; position: absolute; top: -25px; left: 0; right: 0; height: 2px;
}
.theme--dark .org-nodes-wrapper::before { background: #2a2a2a; }
.theme--light .org-nodes-wrapper::before { background: #e0e0e0; }
.org-level:first-child .org-nodes-wrapper::before { display: none; }

.org-node { position: relative; min-width: 180px; max-width: 200px; }
.org-node::before {
  content: ''; position: absolute; top: -25px; left: 50%; transform: translateX(-50%);
  width: 2px; height: 25px;
}
.theme--dark .org-node::before { background: #2a2a2a; }
.theme--light .org-node::before { background: #e0e0e0; }
.org-level:first-child .org-node::before { display: none; }

.org-card { border-radius: 12px !important; transition: transform 0.2s; }
.theme--dark .org-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }
.admin-node .org-card   { border-left: 3px solid #22d3ee !important; }
.manager-node .org-card { border-left: 3px solid #60a5fa !important; }
.user-node .org-card    { border-left: 3px solid #4ade80 !important; }
.org-card:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0,0,0,0.25) !important; }
</style>
